import { MONO_FONT_FAMILY } from '@medcom/shared-ui/styles/globalCss.ts';
import { ConfigProvider } from 'antd';
import deDE from 'antd/es/locale/de_DE';
import type React from 'react';
import { type ReactNode, useEffect } from 'react';
import { useCurrentContextStore } from './hooks/store/useCurrentContextStore.ts';
import { defaultTheme, useThemeStore } from './hooks/store/useThemeStore.ts';
import { useDoctorTheme } from './hooks/useDoctorTheme.ts';

export const DynamicConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentDoctorId } = useCurrentContextStore();
  const { theme, setTheme, previewMode } = useThemeStore();
  const doctorTheme = useDoctorTheme(currentDoctorId);

  useEffect(() => {
    if (!previewMode && theme.id !== doctorTheme?.id) {
      setTheme(doctorTheme ?? defaultTheme, false);
    }
  }, [doctorTheme, theme, setTheme, previewMode]);

  return (
    <ConfigProvider
      locale={deDE}
      modal={{
        mask: {
          blur: false,
        },
      }}
      drawer={{
        mask: {
          blur: false,
        },
      }}
      theme={{
        token: {
          colorLink: '#4A88CB',
          colorPrimary: theme.colorPrimary,
          fontFamily: 'Inter',
          fontFamilyCode: MONO_FONT_FAMILY,
          controlItemBgActive: theme.controlItemBgActive,
          controlItemBgActiveHover: theme.controlItemBgActiveHover,
        },
        components: {
          Layout: {
            headerBg: theme.colorBgHeader,
            bodyBg: '#fafafa',
            triggerBg: theme.colorBgTrigger,
          },
          Menu: {
            itemBg: '#fafafa',
            subMenuItemBg: '#fafafa',
          },
          Table: {
            rowHoverBg: '#f0f0f0', // gray-4, LC-700
            // antd's 8px runs content-sized columns together; the large size's 16px for every size
            cellPaddingInlineMD: 16,
            cellPaddingInlineSM: 16,
          },
        },
      }}
    >
      {children}
    </ConfigProvider>
  );
};
